import moment, { Moment } from 'moment-timezone'

// Helpers for constructing and normalizing Moment values in the tenant's timezone.
// For display formatting, use ./formatTenantDateTime.

// "Now" in the tenant's wall clock.
export const tenantNow = (tz: string): Moment => moment.tz(tz)

// "Tomorrow at the current time" — common default for form fields like
// "start_date" or "delivery_date".
export const tenantTomorrow = (tz: string): Moment => moment.tz(tz).add(1, 'days')

// Parse any input (UTC ISO string, Date, Moment, epoch ms) into a Moment
// displayed in tenant TZ. Returns null for empty input.
export const inTenantTZ = (
  input: string | number | Date | Moment | null | undefined,
  tz: string
): Moment | null => {
  if (input == null || input === '') return null
  return moment.tz(input as moment.MomentInput, tz)
}

// Re-interpret a Moment's wall-clock components in the tenant TZ.
// Use this in MUI X picker `onChange` handlers — without it, a fresh pick
// produces a Moment in browser-local TZ, so a remote admin in Riga picking
// "10:00" submits "10:00 Riga" instead of "10:00 tenant".
// Idempotent when the input is already a tenant-TZ Moment.
export const normalizeToTenantTZ = (m: Moment | null, tz: string): Moment | null => {
  if (!m) return null
  return moment.tz(
    [m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second()],
    tz
  )
}

export const uses12HourClock = (timeFormat?: string): boolean =>
  (timeFormat ?? '').includes('h')

export const withoutSeconds = (timeFormat: string): string =>
  timeFormat.replace(/[:.]?ss?/i, '').trim()

// Day and month only, keeping the location's ordering and separator - a calendar cell has no
// room for the year, but it must not silently fall back to day-month for a location that
// writes month-day.
export const withoutYear = (dateFormat: string): string =>
  dateFormat.replace(/[,./\-\s]*Y+/i, '').replace(/[,.\s]+$/, '').trim()
